import React, { useState, useRef, ReactNode } from "react";
import { Globe, List, XLg } from "react-bootstrap-icons";
import { useRecoilState } from "recoil";
import { langState } from "./recoilAtom";
import Logo from "../components/logo";
import {
  Button,
  FieldError,
  Header as AriaHeader,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Section,
  Select,
  SelectValue,
  Text,
} from "react-aria-components";
import { useHover } from "react-aria";
import { Link } from "gatsby";
import { ContentWrapper } from "./layout";
import Dropdownlink from "./dropdownLink/dropdownlink";
import { StaticImage } from "gatsby-plugin-image";
import EUflag from "../images/EUflag.jpg";
import DropdownlinkMobile from "./dropdownLink/dropdownlinkmobile";

interface HeaderProps {
  className?: string;
}

interface linkItemProps {
  text: string | ReactNode;
  url: string;
  dropdown?: any;
}

interface linksDataProps {
  [lang: string]: linkItemProps[];
}

export const linksTopBar: linksDataProps = {
  "pl-PL": [
    {
      text: "Partnering",
      url: "/partners",
    },

    {
      text: "Inwestorzy",
      url: "/investors/main",
    },
    {
      text: "Kariera",
      url: "/careers",
    },
    {
      text: "Kontakt",
      url: "/contact",
    },
  ],
  "en-US": [
    {
      text: "Partners",
      url: "/partners",
    },
    {
      text: "Investors",
      url: "/investors/main",
    },
    {
      text: "Careers",
      url: "/careers",
    },
    {
      text: "Contact",
      url: "/contact",
    },
  ],
};

export const linksData: linksDataProps = {
  "pl-PL": [
    {
      text: "inPROBE Dx",
      url: "/inprobe",
      dropdown: [
        {
          textDropdown: "Dane kliniczne",
          urlDropdown: "/clinicalstudy",
        },
        {
          textDropdown: "Dane przedkliniczne",
          urlDropdown: "/preclinicaldata",
        },
      ],
    },
    {
      text: "Przeciwciała",
      url: "/antibodies",
    },
    {
      text: "Wskazania",
      url: "/diseaseareas",
      dropdown: [
        {
          textDropdown: "Rak piersi",
          urlDropdown: "/breastcancer",
        },
        {
          textDropdown: "Rak żołądka",
          urlDropdown: "/gastriccancer",
        },
      ],
    },
    {
      text: "O nas",
      url: "/about",
    },
    {
      text: "Media",
      url: "/media",
      dropdown: [
        {
          textDropdown: "Aktualności",
          urlDropdown: "/newsroom",
        },
        {
          textDropdown: "Media materiały",
          urlDropdown: "/mediakit",
        },
      ],
    },
    {
      text: (
        <>
          Projekty{" "}
          <img
            src={EUflag}
            alt="EU flag"
            className="flex min-w-4 min-h-4 w-auto lg:h-full h-auto max-h-16 footer-flag"
            style={{ marginLeft: "4px" }}
          />
        </>
      ),
      url: "/about/grants-and-funding",
    },
  ],
  "en-US": [
    {
      text: "inPROBE Dx",
      url: "/inprobe",
      dropdown: [
        {
          textDropdown: "Clinical study",
          urlDropdown: "/clinicalstudy",
        },
        {
          textDropdown: "Preclinical data",
          urlDropdown: "/preclinicaldata",
        },
      ],
    },
    {
      text: "Antibodies",
      url: "/antibodies",
    },
    {
      text: "Disease areas",
      url: "/diseaseareas",
      dropdown: [
        {
          textDropdown: "Breast cancer",
          urlDropdown: "/breastcancer",
        },
        {
          textDropdown: "Gastric cancer",
          urlDropdown: "/gastriccancer",
        },
      ],
    },
    {
      text: "About",
      url: "/about",
    },
    {
      text: "Media",
      url: "/media",
      dropdown: [
        {
          textDropdown: "Newsroom",
          urlDropdown: "/newsroom",
        },
        {
          textDropdown: "Media Kit",
          urlDropdown: "/mediakit",
        },
      ],
    },
    {
      text: (
        <>
          Projects{" "}
          <img
            src={EUflag}
            alt="EU flag"
            className="flex min-w-4 min-h-4 w-auto lg:h-full h-auto max-h-16 footer-flag"
            style={{ marginLeft: "4px" }}
          />
        </>
      ),
      url: "/about/grants-and-funding",
    },
  ],
};

const LangSwitcherData = [
  {
    langCode: "pl-PL",
    text: (
      <>
        <span> Polish </span> | Polski
      </>
    ),
  },
  {
    langCode: "en-US",
    text: (
      <>
        <span> English </span> | Angielski
      </>
    ),
  },
];

const Header: React.FC<HeaderProps> = (props) => {
  const menuRef = useRef(null) as any;
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [lang, setLang] = useRecoilState(langState);
  const [isHamburegerMenuOpen, setHamburgerMenuOpen] = useState(false);

  const closeLangMenu = (e: any) => {
    !menuRef.current?.contains(e.target) && setMenuOpen(false);
  };

  React.useEffect(() => {
    document.addEventListener("click", closeLangMenu);
  }, []);

  const { hoverProps, isHovered } = useHover(props as any);

  return (
    <header className={props.className}>
      {isHamburegerMenuOpen === true ? (
        <div className="hamburger-menu">
          <button
            className="mobile-close-button"
            onClick={() => setHamburgerMenuOpen(false)}
          >
            <XLg />
          </button>

          <div className="hamburger-logo-container">
            <Link to="/" style={{ textDecoration: "none" }}>
              <Logo />
            </Link>
          </div>

          <ul className="!gap-0 !pt-[108px]">
            {linksData[lang].map((linkItem, idx) => (
              <>
                <DropdownlinkMobile
                  key={idx + "link-item"}
                  onClick={() =>
                    window.location.pathname.endsWith(`${linkItem.url}/`) &&
                    setHamburgerMenuOpen(false)
                  }
                  linkItem={linkItem}
                />
              </>
            ))}
            {linksTopBar[lang].map((linkItem, idx) => (
              <DropdownlinkMobile key={idx + "top-bar"} linkItem={linkItem} />
            ))}
          </ul>

          <div
            className="hamburger-menu-language-selector"
            style={{ justifyContent: "space-between" }}
          >
            <Globe />
            <Select
              selectedKey={lang}
              onSelectionChange={(item: any) => {
                setLang(item);
                setHamburgerMenuOpen(false);
              }}
            >
              <Button>
                <SelectValue />
                <span
                  aria-hidden="true"
                  style={{ transform: "rotate(180deg)" }}
                >
                  ▼
                </span>
              </Button>
              <Popover>
                <ListBox items={LangSwitcherData}>
                  {(item) => (
                    <ListBoxItem id={item.langCode}>{item.text}</ListBoxItem>
                  )}
                </ListBox>
              </Popover>
            </Select>
          </div>
        </div>
      ) : (
        ""
      )}
      <div className="header-top-bar">
        <ContentWrapper>
          <ul className="topbar-menu-items">
            {linksTopBar[lang].map((linkItem, idx) => (
              <li className="topbar-menu-item" key={idx + "top-bar2"}>
                <Link activeClassName="active" to={linkItem.url}>
                  {linkItem.text}
                </Link>
              </li>
            ))}
          </ul>
        </ContentWrapper>
      </div>

      <div className="layout-content-header !max-h-[88px]">
        <Link className="logo-wrapper" to="/">
          <Logo />
        </Link>
        <ul>
          {linksData[lang].map((linkItem, idx) => (
            <>
              <Dropdownlink linkItem={linkItem} key={idx + "drodown-link"} />
            </>
          ))}
        </ul>
        <div className="hamburger-menu-wrapper">
          <button
            onClick={() => setHamburgerMenuOpen(true)}
            style={{ all: "unset" }}
          >
            <List />
          </button>

          <div style={{ position: "relative" }} ref={menuRef}>
            {LangSwitcherData.map((item, idx) =>
              lang === item.langCode ? (
                <button
                  key={idx + "switcher"}
                  className="icon-button"
                  onClick={() => setLang(lang === "pl-PL" ? "en-US" : "pl-PL")}
                >
                  <Globe className="hide-mobile" />

                  {lang === "pl-PL" ? "PL" : "EN"}
                </button>
              ) : null
            )}
          </div>
        </div>

        <div
          className="hide-mobile"
          style={{ position: "relative" }}
          ref={menuRef}
        >
          {LangSwitcherData.map((item, idx) =>
            lang === item.langCode ? (
              <button
                key={idx + "switcher2"}
                className="icon-button"
                onClick={() => setMenuOpen(!isMenuOpen)}
              >
                <Globe className="hide-mobile" />

                {lang === "pl-PL" ? "PL" : "EN"}
              </button>
            ) : null
          )}
          {isMenuOpen ? (
            <ul className="language-switcher-wrapper">
              {LangSwitcherData.map((item, idx) => (
                <li
                  key={idx + "switcher3"}
                  onClick={() => {
                    setLang(item.langCode);
                    setMenuOpen(false);
                  }}
                >
                  {item.text}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </header>
  );
};

export default Header;
